// Aba 2 · Final — @remotion/player com visual.props sobre o cut.mp4 (preview = render).
// Edições mudam as props localmente (Player na hora), com desfazer/refazer; Salvar
// manda PUT /jobs/:id/visual {props, baseRevision} e o backend re-renderiza o MP4.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EdvidJobState, StyleSetup } from '../../../types/videoEdit';
import type { VisualProps } from './demoProps';
import { withVideo } from './demoProps';
import { ReelsPreview } from './ReelsPreview';
import { usePlayhead, usePlayheadLoop, type MediaController } from './playhead';
import { useTimelineZoom } from './useTimelineZoom';
import { useHistory } from './useHistory';
import { Transport } from './Transport';
import { FinalTimeline, groupCaptions, type FinalSel } from './FinalTimeline';
import { FinalInspector } from './FinalInspector';
import { NotesPanel, type EdvidNotesApi } from './notes';
import { BTN, BTN_PRIMARY, Panel, StageLayout, useKeys } from './ui';
import { IconDownload, IconRedo, IconUndo } from './icons';

export type VisualSaveResult = 'ok' | 'conflict' | 'error';

export function FinalTab({ edvid, notes, onSave, onRestyle, restyling, onDirtyChange, onApplyNotes, applyingNotes, toast: flash, publish }: {
  edvid: EdvidJobState;
  notes: EdvidNotesApi;
  onSave: (props: VisualProps, baseRevision: number) => Promise<VisualSaveResult>;
  onRestyle: (style: StyleSetup) => void;
  restyling: boolean;
  onDirtyChange: (n: number) => void;
  onApplyNotes: () => void;
  applyingNotes: boolean;
  toast: (msg: string) => void;
  publish?: React.ReactNode;
}) {
  const cut = edvid.cut!;
  const visual = edvid.visual!;
  const hist = useHistory<VisualProps>(visual.props);
  const [saved, setSaved] = useState<VisualProps>(visual.props);
  const [baseRevision, setBaseRevision] = useState(visual.revision);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState<FinalSel>(null);
  const [controller, setController] = useState<MediaController | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const playhead = usePlayhead();
  const props = hist.present;
  const dirty = props !== saved;
  const rendering = edvid.phase === 'visual_rendering';

  useEffect(() => { onDirtyChange(dirty ? 1 : 0); }, [dirty, onDirtyChange]);

  // revisão nova do servidor: adota se não há edição local
  const lastRev = useRef(visual.revision);
  useEffect(() => {
    if (lastRev.current === visual.revision) return;
    lastRev.current = visual.revision;
    if (!dirty || conflict) return;
    hist.reset(visual.props);
    setSaved(visual.props);
    setBaseRevision(visual.revision);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visual.revision]);

  const ed = props.editData;
  const duration = ed.durationSec || cut.durationSec;
  const portrait = (ed.height || 1920) >= (ed.width || 1080);
  const reelsProps = useMemo(() => withVideo(props, cut.url), [props, cut.url]);
  const lines = useMemo(() => groupCaptions(props.captions, ed.captions.maxWords || 3), [props.captions, ed.captions.maxWords]);

  const identity = useCallback((t: number) => t, []);
  usePlayheadLoop(controller, identity, playhead);
  const zoom = useTimelineZoom(panelRef, duration, () => playhead.get());
  const seek = useCallback((t: number) => { const tt = Math.max(0, Math.min(duration, t)); controller?.seek(tt); playhead.set(tt); }, [controller, duration, playhead]);

  // animação / atrás da pessoa selecionada (na timeline ou recém-adicionada): leva a agulha
  // para dentro da janela, já com a entrada assentada (~8 quadros), para o Player mostrar
  const selKey = sel?.kind === 'graphic' || sel?.kind === 'behind' ? `${sel.kind}:${sel.index}` : '';
  useEffect(() => {
    if (!sel || (sel.kind !== 'graphic' && sel.kind !== 'behind')) return;
    const win = sel.kind === 'graphic'
      ? (() => { const g = ed.graphics?.[sel.index]; return g ? { start: g.start, end: g.end } : null; })()
      : (() => { const b = ed.behind?.[sel.index]; return b ? { start: b.start, end: b.start + b.dur } : null; })();
    if (!win) return;
    const t = playhead.get();
    if (t < win.start + 0.25 || t > win.end) seek(Math.min(win.end - 0.05, win.start + 0.6));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey]);

  const mark = useCallback(() => {
    const msg = notes.toggleMark(playhead.get(), 'visual', identity);
    if (msg) flash(msg);
  }, [notes, playhead, identity, flash]);

  const save = useCallback(async () => {
    if (!dirty || saving) return;
    setSaving(true);
    const snapshot = props;
    const r = await onSave(snapshot, baseRevision);
    setSaving(false);
    if (r === 'ok') { setSaved(snapshot); setConflict(false); }
    else if (r === 'conflict') setConflict(true);
  }, [dirty, saving, props, baseRevision, onSave]);

  useKeys(useCallback((e: KeyboardEvent) => {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) hist.redo(); else hist.undo(); return; }
    if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); hist.redo(); return; }
    if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); void save(); return; }
    if (e.key === 'm' || e.key === 'M') { e.preventDefault(); mark(); return; }
    if ((e.key === 'Delete' || e.key === 'Backspace') && (sel?.kind === 'graphic' || sel?.kind === 'behind')) {
      e.preventDefault();
      const { kind, index } = sel;
      hist.commit(p => kind === 'graphic'
        ? { ...p, editData: { ...p.editData, graphics: (p.editData.graphics || []).filter((_, i) => i !== index) } }
        : { ...p, editData: { ...p.editData, behind: (p.editData.behind || []).filter((_, i) => i !== index) } });
      setSel(null);
      return;
    }
    if (e.key === 'Escape') { if (notes.pendingIn != null) { notes.setPendingIn(null); flash('IN cancelado'); } else setSel(null); return; }
    if (e.code === 'Space') { e.preventDefault(); if (!controller) return; if (controller.isPaused()) controller.play(); else controller.pause(); return; }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const step = e.shiftKey ? 1 : 1 / (ed.fps || 30);
      seek(playhead.get() + (e.key === 'ArrowRight' ? step : -step));
    }
  }, [hist, save, mark, notes, flash, controller, ed.fps, seek, playhead, sel]));

  const player = (
    <>
      <ReelsPreview props={reelsProps} onController={setController} />
      {rendering && (
        <div className="pointer-events-none absolute inset-x-2 top-2 z-10 rounded-xl bg-slate-950/85 px-3 py-2 text-center text-[11px] font-bold text-indigo-200">
          Renderizando o MP4 final… a prévia já mostra o resultado.
        </div>
      )}
    </>
  );

  return (
    <StageLayout portrait={portrait} player={player}>
      <Transport controller={controller} playhead={playhead} total={duration} markArmed={notes.pendingIn != null} onMark={mark} slider={zoom.slider} onSlider={zoom.setSlider} onFit={zoom.fit}>
        <div className="ml-2 flex items-center gap-1">
          <button type="button" onClick={hist.undo} disabled={!hist.canUndo} className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-300 hover:bg-white/10 disabled:opacity-30" title="Desfazer (Ctrl+Z)" aria-label="Desfazer"><IconUndo /></button>
          <button type="button" onClick={hist.redo} disabled={!hist.canRedo} className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-300 hover:bg-white/10 disabled:opacity-30" title="Refazer (Ctrl+Shift+Z)" aria-label="Refazer"><IconRedo /></button>
        </div>
        {dirty && (
          <div className="ml-1 flex items-center gap-2">
            <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-[11px] font-black text-amber-300">alterações não salvas</span>
            <button type="button" className={BTN} onClick={() => { hist.reset(saved); setSel(null); }} disabled={saving}>Descartar</button>
            <button type="button" className={BTN_PRIMARY} onClick={() => void save()} disabled={saving}>{saving ? 'Salvando…' : 'Salvar e renderizar'}</button>
          </div>
        )}
      </Transport>

      {conflict && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-rose-400/30 bg-rose-500/10 px-3 py-2">
          <p className="flex-1 text-xs text-rose-100">O visual mudou em outro lugar (revisão {visual.revision}). Escolha qual versão fica.</p>
          <button type="button" className="rounded-xl border border-rose-400/40 px-3 py-2 text-xs font-black text-rose-200 hover:bg-rose-400/10" onClick={() => { hist.reset(visual.props); setSaved(visual.props); setBaseRevision(visual.revision); setConflict(false); }}>Carregar atual</button>
          <button type="button" className="rounded-xl border border-amber-400/40 px-3 py-2 text-xs font-black text-amber-200 hover:bg-amber-400/10" onClick={() => { setBaseRevision(visual.revision); setConflict(false); flash('Suas edições foram mantidas e serão salvas sobre a revisão atual.'); }}>Manter minhas edições</button>
        </div>
      )}

      <FinalTimeline
        props={props}
        savedProps={saved}
        duration={duration}
        sel={sel}
        onSelect={s => {
          setSel(s);
          if (s?.kind === 'caption' && lines[s.line]) seek(lines[s.line].start + 0.01);
        }}
        panelRef={panelRef}
        pps={zoom.pps}
        playhead={playhead}
        onSeek={seek}
        notes={notes.all.filter(n => n.phase === 'visual')}
        pendingIn={notes.pendingIn}
        onOpenNote={id => notes.setEditing({ id, isNew: false })}
        onBegin={hist.beginGesture}
        onGesture={hist.gesture}
        onEnd={hist.endGesture}
      />

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <FinalInspector
          key={sel ? JSON.stringify(sel) : `none-${visual.revision}`}
          props={props}
          sel={sel}
          lines={lines}
          style={visual.style}
          onBegin={hist.beginGesture}
          onGesture={hist.gesture}
          onEnd={hist.endGesture}
          onCommit={hist.commit}
          onRestyle={onRestyle}
          onSelect={setSel}
          restyling={restyling}
          now={() => playhead.get()}
          hookOptions={visual.hookOptions}
        />
        <div className="space-y-3">
          <Panel title={`Vídeo final · revisão ${visual.revision}`}>
            <div className="flex flex-wrap items-center gap-2">
            {publish}
            {visual.finalUrl ? (
              <a href={visual.finalUrl} download className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-xs font-black text-slate-950 hover:bg-emerald-400">
                <IconDownload /> Baixar MP4
              </a>
            ) : (
              <p className="text-[11px] text-slate-400">{rendering ? 'O MP4 está sendo renderizado.' : 'Ainda sem MP4 renderizado.'}</p>
            )}
            </div>
            {visual.renderedAt && <p className="mt-1.5 text-[10px] text-slate-500">renderizado em {new Date(visual.renderedAt).toLocaleString('pt-BR')}</p>}
            {visual.warnings?.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[11px] text-amber-200">{visual.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            )}
          </Panel>
          <NotesPanel
            notes={notes.all}
            phase="visual"
            isLocal={notes.isLocal}
            onOpen={id => notes.setEditing({ id, isNew: false })}
            onSeek={seek}
            onApply={onApplyNotes}
            applying={applyingNotes}
          />
        </div>
      </div>
    </StageLayout>
  );
}
